import React, { useEffect, useState } from "react";
import { Control } from "react-hook-form";

import Button from "~/shared/ui/Fields/Button";
import Modal from "~/shared/ui/Fields/Modal";
import { DeletingCourse, LevelCourse } from "~/pages/Editor/EditorCatalogEducationalPrograms/EditorPositionsAndTrainingPrograms/ProgramsManagement/types";
import {
  Course as TCourse,
  TLevel,
} from "~/pages/Editor/EditorCatalogEducationalPrograms/types";
import { StatusOptions } from "~/shared/lib/statuses";

import AddCourseMenu from "./AddCourseMenu";
import { MemoizedCourse } from "./Course";
import { courseValidation } from "./utils";

import s from "./Level.less";

type LevelProps = {
  level: TLevel;
  control: Control<Record<string, any>>;
  courses: TCourse[];
  addCourseToCollection: (levelId, course) => void;
  deleteCourseFromCollection: (levelId, course) => void;
  updateCourseInCollection: (levelId, course, oldCourseId) => void;
  isViewing: boolean;
  usedCoursesId: string[];
  watch: any;
  refetch;
};

const Level = ({
  level,
  courses,
  control,
  addCourseToCollection,
  deleteCourseFromCollection,
  updateCourseInCollection,
  isViewing,
  usedCoursesId,
  watch,
  refetch,
}: LevelProps) => {
  const [currentCourses, setCurrentCourses] = useState([]);
  const [isOpenAddCourseModal, setIsOpenCourseModal] = useState(false);
  const [selectorValue, setSelectorValue] = useState<LevelCourse>();
  const [courseStatusError, setCourseStatusError] = useState(null);

  const currentCourseCertificationPeriodWatcher = watch(
    `${level.id}course`
  )?.certificateValidityPeriod;

  const handleOpenAddCourseModal = () => setIsOpenCourseModal(true);
  const handleCloseAddCourseModal = () => setIsOpenCourseModal(false);
  const activeLevel = "active" === level.status;

  const handleOnChangeCourse = (value, fieldName) => {
    setSelectorValue((prevState) => ({
      ...prevState,
      id: level.id,
      [fieldName]: value,
    }));
  };

  const handleAddCourse = () => {
    const isValid = courseValidation({
      selectorValue,
      setCourseStatusError,
    });
    if (
      selectorValue &&
      selectorValue.course &&
      selectorValue.status &&
      selectorValue.id &&
      isValid
    ) {
      addCourseToCollection(selectorValue.id, selectorValue);
      setSelectorValue(null);
      setCourseStatusError(null);
      setIsOpenCourseModal(false);
    }
  };

  const handleDeleteCourse = (course: DeletingCourse) => {
    const levelId = level.id;
    deleteCourseFromCollection(levelId, course);
  };

  const handleChangeCourse = (id: string) => {
    const currentCourse = level.courses.find((course) => course.id === id);

    const updatedCourse = {
      ...currentCourse,
      course: { label: currentCourse.name, value: currentCourse.id },
      status: {
        label: currentCourse.status === "active" ? "Активный" : "Неактивный",
        value: currentCourse.status,
      },
    };

    const levelId = level.id;
    const newCourse = {
      ...currentCourse,
      id: levelId,
      course: selectorValue?.course || updatedCourse.course,
      status: selectorValue?.status || updatedCourse.status,
    };

    const isValid = courseValidation({
      selectorValue: newCourse,
      setCourseStatusError,
    });

    if (isValid) {
      updateCourseInCollection(levelId, newCourse, id);
      setSelectorValue(null);
      setCourseStatusError(null);
    }
  };

  const handleDelete = () => {
    setSelectorValue(null);
    setIsOpenCourseModal(false);
  };

  useEffect(() => {
    if (selectorValue && selectorValue.status) {
      setCourseStatusError(null);
    }
  }, [selectorValue]);

  useEffect(() => {
    courses && setCurrentCourses(courses);
  }, [courses]);

  return (
    <div className={s.level}>
      <div className={s.title_wrapper}>
        <span className={s.level_title}>{level.name}</span>
        {isViewing && (
          <span className={s.level_status}>
            {StatusOptions[level.status]} - уровень
          </span>
        )}
      </div>
      <ul className={s.items}>
        {currentCourses?.map((course) => (
          <MemoizedCourse
            key={course.id}
            course={course}
            control={control}
            handleDeleteCourse={handleDeleteCourse}
            handleChangeCourse={handleChangeCourse}
            handleOnChangeCourse={handleOnChangeCourse}
            courseStatusError={courseStatusError}
            watch={watch}
            usedCoursesId={usedCoursesId}
            levelId={level.id}
            level={level}
            refetch={refetch}
          />
        ))}
      </ul>
      {isOpenAddCourseModal && (
        <Modal
          isOpen={isOpenAddCourseModal}
          actionClose={handleCloseAddCourseModal}
          title={"Создание курса"}
        >
          <AddCourseMenu
            level={level}
            control={control}
            handleOnChangeCourse={handleOnChangeCourse}
            currentCourseCertificationPeriodWatcher={
              currentCourseCertificationPeriodWatcher
            }
            courseStatusError={courseStatusError}
            handleAddCourse={handleAddCourse}
            handleDelete={handleDelete}
            setCourseStatusError={setCourseStatusError}
            watch={watch}
            usedCoursesId={usedCoursesId}
            levelId={level.id}
          />
        </Modal>
      )}
      {!isViewing && (
        <Button
          small={true}
          txtBtn={activeLevel ? "Добавить курс" : "Неактивный уровень"}
          onClick={handleOpenAddCourseModal}
          disabled={!activeLevel}
        />
      )}
    </div>
  );
};

export const MemoizedLevel = React.memo(Level);
